/*
* Copyright (c) 2023 Eugenio Espinoza
*
* Thin client for the public iTunes Search API.
* No auth, CORS-friendly, cached in sessionStorage for the session.
*/

import { useEffect, useState, useMemo } from 'react';

const CACHE_TTL_MS = 1000 * 60 * 30; // 30 minutes per session

function upsizeArtwork(url, size = 1000) {
  if (!url) return null;
  return url.replace(/\/\d{2,4}x\d{2,4}(bb)?\./, `/${size}x${size}$1.`);
}

function normalizeCollection(r) {
  return {
    id: String(r.collectionId),
    title: r.collectionName,
    artist: r.artistName,
    artistId: String(r.artistId),
    releaseDate: r.releaseDate,
    trackCount: r.trackCount,
    artworkSmall: r.artworkUrl100,
    artworkMedium: upsizeArtwork(r.artworkUrl100, 600),
    artworkLarge: upsizeArtwork(r.artworkUrl100, 1200),
    appleUrl: r.collectionViewUrl,
    type: r.collectionType,            // "Album" | "EP" | ...
    copyright: r.copyright,
    genre: r.primaryGenreName,
  };
}

function normalizeTrack(r) {
  return {
    id: String(r.trackId),
    number: r.trackNumber,
    name: r.trackName,
    lengthMs: r.trackTimeMillis,
    previewUrl: r.previewUrl,
    appleUrl: r.trackViewUrl,
  };
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { t, v } = JSON.parse(raw);
    if (Date.now() - t > CACHE_TTL_MS) return null;
    return v;
  } catch { return null; }
}

function writeCache(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }));
  } catch { /* storage full or disabled — ignore */ }
}

/*
 * iTunes' lookup/search endpoints sit behind a CDN that caches the CORS
 * `Access-Control-Allow-Origin` header without varying on the request Origin,
 * so a browser `fetch()` is intermittently blocked ("Failed to fetch"). The
 * endpoint does support JSONP via `&callback=`, and a <script> tag is not
 * subject to CORS — so we load it that way instead.
 */
let jsonpSeq = 0;

function fetchJson(url, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    const cbName = `__itunesCb_${Date.now()}_${jsonpSeq++}`;
    const sep = url.includes('?') ? '&' : '?';
    const script = document.createElement('script');
    let timer = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      delete window[cbName];
      if (script.parentNode) script.parentNode.removeChild(script);
    };

    window[cbName] = (data) => {
      cleanup();
      resolve(data);
    };

    script.onerror = () => {
      cleanup();
      reject(new Error('iTunes request failed'));
    };

    timer = setTimeout(() => {
      cleanup();
      reject(new Error('iTunes request timed out'));
    }, timeoutMs);

    script.src = `${url}${sep}callback=${cbName}`;
    document.body.appendChild(script);
  });
}

/**
 * Fetch releases for one or more Apple Music artist IDs in a single request.
 * Returns a { loading, releases[], byArtist{id: releases[]}, latest, error } shape.
 */
export function useItunesReleases(appleArtistIds, limit = 10) {
  const idsKey = useMemo(
    () => (Array.isArray(appleArtistIds) ? appleArtistIds : [appleArtistIds])
      .filter(Boolean)
      .map(String)
      .join(','),
    [appleArtistIds]
  );

  const [state, setState] = useState({
    loading: true,
    releases: [],
    byArtist: {},
    latest: null,
    error: null,
  });

  useEffect(() => {
    if (!idsKey) {
      setState({ loading: false, releases: [], byArtist: {}, latest: null, error: null });
      return;
    }

    const cacheKey = `mt-itunes-${idsKey}-${limit}`;
    const cached = readCache(cacheKey);
    if (cached) {
      setState({ loading: false, error: null, ...cached });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJson(
          `https://itunes.apple.com/lookup?id=${idsKey}&entity=album&sort=recent&limit=${limit}`
        );
        const releases = (data.results || [])
          .filter((r) => r.wrapperType === 'collection')
          .map(normalizeCollection)
          .sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));

        const byArtist = {};
        releases.forEach((r) => {
          if (!byArtist[r.artistId]) byArtist[r.artistId] = [];
          byArtist[r.artistId].push(r);
        });

        const payload = { releases, byArtist, latest: releases[0] || null };
        writeCache(cacheKey, payload);
        if (!cancelled) setState({ loading: false, error: null, ...payload });
      } catch (err) {
        if (!cancelled) {
          setState({ loading: false, releases: [], byArtist: {}, latest: null, error: err });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [idsKey, limit]);

  return state;
}

/**
 * Fetch the tracklist for a single album collectionId.
 */
export function useItunesTracks(collectionId) {
  const [state, setState] = useState({ loading: true, tracks: [], error: null });

  useEffect(() => {
    if (!collectionId) {
      setState({ loading: false, tracks: [], error: null });
      return;
    }

    const cacheKey = `mt-itunes-tracks-${collectionId}`;
    const cached = readCache(cacheKey);
    if (cached) {
      setState({ loading: false, error: null, tracks: cached });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJson(
          `https://itunes.apple.com/lookup?id=${collectionId}&entity=song&limit=50`
        );
        const tracks = (data.results || [])
          .filter((r) => r.wrapperType === 'track')
          .map(normalizeTrack)
          .sort((a, b) => (a.number || 0) - (b.number || 0));
        writeCache(cacheKey, tracks);
        if (!cancelled) setState({ loading: false, tracks, error: null });
      } catch (err) {
        if (!cancelled) setState({ loading: false, tracks: [], error: err });
      }
    })();

    return () => { cancelled = true; };
  }, [collectionId]);

  return state;
}

export function formatReleaseDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-MX', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return ''; }
}

export function formatDuration(ms) {
  if (!ms) return '';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}
